import { useCallback, useEffect, useState } from 'react';
import clsx from 'clsx';
import {
  BerichtError,
  RICHTING_LABELS,
  fetchBerichtDetail,
  fetchBerichtenByReparatie,
  fetchBerichtenByWerkorder,
  refreshFotoUrl,
  replyBericht,
  resolveBericht,
  reopenBericht,
  uploadBerichtFoto,
  type BerichtDetail,
  type BerichtSummary,
  type Richting,
} from '../../lib/berichten';
import { formatDateTime } from '../../lib/format';
import { BerichtStatusPill } from './BerichtStatusPill';
import { BerichtComposer } from './BerichtComposer';

interface Props {
  /** Field-scope (default). Verplicht tenzij peer='reparateur'. */
  werkOrderId?: string;
  /** Beperkt tot berichten die aan deze WI hangen, plus WO-niveau berichten. */
  wasserijItemId?: string | null;
  werkOrderName?: string | null;
  /** 'field' = chat met uitvoering (default); 'reparateur' = chat met de reparateur. */
  peer?: 'field' | 'reparateur';
  /** Verplicht bij peer='reparateur': de Reparatie_Opdracht__c-id. */
  reparatieOpdrachtId?: string;
}

/**
 * Inline berichten-thread. Twee modes:
 * - peer='field' (default): chat met de uitvoering, gescopet op een Werk_Order
 *   (optioneel een Wasserij_item). Gebruikt onderaan OrderDetailView /
 *   WasserijItemDetailView.
 * - peer='reparateur': chat met de reparateur, gescopet op een Reparatie_Opdracht.
 *   Gebruikt in ReparatieSection.
 */
export function BerichtThread({
  werkOrderId,
  wasserijItemId,
  werkOrderName,
  peer = 'field',
  reparatieOpdrachtId,
}: Props) {
  const [berichten, setBerichten] = useState<BerichtSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openThreadId, setOpenThreadId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showClosed, setShowClosed] = useState(false);

  const isReparateur = peer === 'reparateur';

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows =
        isReparateur && reparatieOpdrachtId
          ? await fetchBerichtenByReparatie(reparatieOpdrachtId, true)
          : werkOrderId
            ? await fetchBerichtenByWerkorder(werkOrderId, wasserijItemId ?? undefined, true)
            : [];
      setBerichten(rows);
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Berichten laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [isReparateur, reparatieOpdrachtId, werkOrderId, wasserijItemId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const visible = showClosed ? berichten : berichten.filter((b) => b.status !== 'afgehandeld');
  const openCount = berichten.filter((b) => b.status === 'open').length;

  return (
    <section className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Berichten</h3>
          {openCount > 0 && (
            <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium bg-amber-100 text-amber-800 border border-amber-200">
              {openCount} open
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowClosed((v) => !v)}
            className="text-[11px] text-slate-500 hover:text-slate-800"
          >
            {showClosed ? 'Verberg afgehandeld' : 'Toon afgehandeld'}
          </button>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="text-[11px] font-medium text-indigo-700 hover:text-indigo-900"
          >
            + Nieuw bericht
          </button>
        </div>
      </div>

      {loading && visible.length === 0 && <p className="text-xs text-slate-500">Laden…</p>}

      {error && (
        <div className="rounded bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">{error}</div>
      )}

      {!loading && visible.length === 0 && !error && (
        <p className="text-xs text-slate-400 italic">Geen openstaande berichten.</p>
      )}

      <ul className="space-y-2">
        {visible.map((bericht) => (
          <BerichtRow
            key={bericht.id}
            bericht={bericht}
            isOpen={openThreadId === bericht.id}
            onToggle={() => setOpenThreadId((cur) => (cur === bericht.id ? null : bericht.id))}
            onChanged={refresh}
          />
        ))}
      </ul>

      {showCreate &&
        (isReparateur && reparatieOpdrachtId ? (
          <BerichtComposer
            reparatieOpdrachtId={reparatieOpdrachtId}
            richting="office_to_reparateur"
            onClose={() => setShowCreate(false)}
            onCreated={() => {
              setShowCreate(false);
              refresh();
            }}
          />
        ) : (
          <BerichtComposer
            werkOrderId={werkOrderId}
            wasserijItemId={wasserijItemId ?? null}
            werkOrderName={werkOrderName ?? null}
            onClose={() => setShowCreate(false)}
            onCreated={() => {
              setShowCreate(false);
              refresh();
            }}
          />
        ))}
    </section>
  );
}

// ─── Rij met inklapbare thread ─────────────────────────────────────────────

interface RowProps {
  bericht: BerichtSummary;
  isOpen: boolean;
  onToggle: () => void;
  onChanged: () => void;
}

function BerichtRow({ bericht, isOpen, onToggle, onChanged }: RowProps) {
  return (
    <li className="border border-slate-200 rounded">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-start gap-2 px-3 py-2 text-left hover:bg-slate-50"
      >
        <div className="flex-1 min-w-0 space-y-0.5">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-slate-900 truncate">
              {bericht.onderwerp || bericht.laatsteReactiePreview || '(geen onderwerp)'}
            </span>
            <BerichtStatusPill status={bericht.status} />
            {bericht.prioriteit === 'urgent' && (
              <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded font-medium bg-red-100 text-red-700 border border-red-200">
                Urgent
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 flex items-center gap-2 flex-wrap">
            {bericht.wasserijItemName && (
              <span className="font-medium text-slate-700">{bericht.wasserijItemName}</span>
            )}
            <span>
              {bericht.aantalReacties} bericht{bericht.aantalReacties === 1 ? '' : 'en'}
            </span>
            <span>·</span>
            <span>{formatDateTime(bericht.updatedAt)}</span>
            {(bericht.richting === 'field_to_office' ||
              bericht.richting === 'reparateur_to_office') && (
              <span className="text-amber-700">· {RICHTING_LABELS[bericht.richting]}</span>
            )}
          </div>
        </div>
        <span className="text-slate-400 text-xs mt-1">{isOpen ? '▴' : '▾'}</span>
      </button>

      {isOpen && <BerichtConversation berichtId={bericht.id} onChanged={onChanged} />}
    </li>
  );
}

// ─── Conversatie (reacties + reply + afhandelen) — herbruikbaar ──────────────

interface ConversationProps {
  berichtId: string;
  onChanged?: () => void;
  /** Toon een border-top (default true voor inline gebruik in een rij). */
  bordered?: boolean;
}

export function BerichtConversation({ berichtId, onChanged, bordered = true }: ConversationProps) {
  const [detail, setDetail] = useState<BerichtDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setDetail(await fetchBerichtDetail(berichtId));
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Detail laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [berichtId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !detail) {
    return <div className={clsx('px-3 py-2 text-xs text-slate-500', bordered && 'border-t border-slate-200')}>Laden…</div>;
  }
  if (error && !detail) {
    return <div className={clsx('px-3 py-2 text-xs text-red-700', bordered && 'border-t border-slate-200')}>{error}</div>;
  }
  if (!detail) return null;

  const canReply = detail.status !== 'afgehandeld';
  // Leid de tegenpartij af uit de thread-richting, zodat reply + bubble-uitlijning
  // ook klopt als deze conversatie standalone (BerichtenPage) wordt getoond.
  const threadIsReparateur =
    detail.richting === 'office_to_reparateur' || detail.richting === 'reparateur_to_office';
  const replyRichting: Richting = threadIsReparateur ? 'office_to_reparateur' : 'office_to_field';

  return (
    <div className={clsx(bordered && 'border-t border-slate-200')}>
      <ul className="px-3 py-2 space-y-2 max-h-80 overflow-y-auto bg-slate-50">
        {detail.reacties.map((r) => {
          const isOffice = r.richting === 'office_to_field' || r.richting === 'office_to_reparateur';
          const isSysteem = r.bron === 'Systeem';
          return (
            <li
              key={r.id}
              className={clsx('flex', isOffice ? 'justify-end' : 'justify-start', isSysteem && 'justify-center')}
            >
              <div
                className={clsx(
                  'max-w-[85%] rounded-lg px-3 py-2 text-xs',
                  isSysteem
                    ? 'bg-slate-200 text-slate-700 text-[11px] italic'
                    : isOffice
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white border border-slate-200 text-slate-800'
                )}
              >
                <p className="whitespace-pre-wrap break-words">{r.body}</p>
                {r.fotoUrls.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {r.fotoUrls.map((path) => (
                      <FotoThumb key={path} path={path} />
                    ))}
                  </div>
                )}
                {!isSysteem && (
                  <div className={clsx('mt-1 text-[10px]', isOffice ? 'text-indigo-100' : 'text-slate-400')}>
                    {r.auteurNaam || r.auteurEmail} · {formatDateTime(r.createdAt)}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      <div className="border-t border-slate-200 px-3 py-2 space-y-2">
        {canReply ? (
          <ReplyForm
            berichtId={detail.id}
            replyRichting={replyRichting}
            onPosted={() => {
              load();
              onChanged?.();
            }}
          />
        ) : (
          <div className="text-xs text-slate-500 italic">Bericht is afgehandeld.</div>
        )}

        <div className="flex items-center justify-end gap-2 pt-1">
          {detail.status === 'afgehandeld' ? (
            <button
              type="button"
              onClick={async () => {
                try {
                  await reopenBericht({ berichtId: detail.id });
                  await load();
                  onChanged?.();
                } catch (err) {
                  setError(err instanceof BerichtError ? err.message : 'Heropenen mislukt');
                }
              }}
              className="text-[11px] text-slate-600 hover:text-slate-900"
            >
              Heropen
            </button>
          ) : (
            <button
              type="button"
              onClick={async () => {
                try {
                  await resolveBericht({ berichtId: detail.id });
                  await load();
                  onChanged?.();
                } catch (err) {
                  setError(err instanceof BerichtError ? err.message : 'Afhandelen mislukt');
                }
              }}
              className="text-[11px] text-emerald-700 hover:text-emerald-900 font-medium"
            >
              Markeer als afgehandeld ✓
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Reply form (tekst + foto's) ─────────────────────────────────────────────

interface ReplyFormProps {
  berichtId: string;
  replyRichting: Richting;
  onPosted: () => void;
}

function ReplyForm({ berichtId, replyRichting, onPosted }: ReplyFormProps) {
  const peerLabel = replyRichting === 'office_to_reparateur' ? 'reparateur' : 'uitvoering';
  const [body, setBody] = useState('');
  const [fotoPaths, setFotoPaths] = useState<string[]>([]);
  const [fotoPreviews, setFotoPreviews] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return;
    setUploading(true);
    setError(null);
    try {
      for (const file of Array.from(files)) {
        const result = await uploadBerichtFoto(file, berichtId);
        setFotoPaths((prev) => [...prev, result.path]);
        setFotoPreviews((prev) => [...prev, result.url]);
      }
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Foto-upload mislukt');
    } finally {
      setUploading(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setPosting(true);
    setError(null);
    try {
      await replyBericht({ berichtId, body: body.trim(), fotoUrls: fotoPaths, richting: replyRichting });
      setBody('');
      setFotoPaths([]);
      setFotoPreviews([]);
      onPosted();
    } catch (err) {
      setError(err instanceof BerichtError ? err.message : 'Reactie mislukt');
    } finally {
      setPosting(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-1.5">
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
        maxLength={5000}
        placeholder={`Reactie aan ${peerLabel}…`}
        className="w-full text-xs border border-slate-300 rounded px-2 py-1.5"
      />

      {fotoPreviews.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {fotoPreviews.map((url, idx) => (
            <div key={fotoPaths[idx]} className="relative">
              <img src={url} alt="" className="w-12 h-12 object-cover rounded border border-slate-200" />
              <button
                type="button"
                onClick={() => {
                  setFotoPaths((prev) => prev.filter((_, i) => i !== idx));
                  setFotoPreviews((prev) => prev.filter((_, i) => i !== idx));
                }}
                className="absolute -top-1 -right-1 bg-white border border-slate-300 rounded-full w-4 h-4 text-[10px] leading-none hover:bg-red-50"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-[11px] text-red-700">{error}</p>}

      <div className="flex items-center justify-between gap-2">
        <label
          className={clsx(
            'inline-flex items-center gap-1 text-[11px] cursor-pointer text-slate-600 hover:text-slate-900',
            uploading && 'opacity-50 cursor-wait'
          )}
        >
          📎 {uploading ? 'Uploaden…' : 'Foto toevoegen'}
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleFiles(e.target.files)}
            className="hidden"
            disabled={uploading}
          />
        </label>

        <button
          type="submit"
          disabled={posting || uploading || !body.trim()}
          className="bg-indigo-600 text-white text-xs font-medium px-3 py-1 rounded hover:bg-indigo-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
        >
          {posting ? 'Versturen…' : 'Reageer'}
        </button>
      </div>
    </form>
  );
}

// ─── Foto thumb met signed-URL refresh ───────────────────────────────────────

function FotoThumb({ path }: { path: string }) {
  const [src, setSrc] = useState<string | null>(path.startsWith('http') ? path : null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    if (path.startsWith('http')) return;
    let cancelled = false;
    refreshFotoUrl(path)
      .then((url) => {
        if (!cancelled) setSrc(url);
      })
      .catch(() => {
        if (!cancelled) setErrored(true);
      });
    return () => {
      cancelled = true;
    };
  }, [path]);

  if (errored) {
    return (
      <span className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-slate-200 text-slate-600">
        foto?
      </span>
    );
  }
  if (!src) {
    return <span className="inline-block w-12 h-12 bg-slate-200 rounded animate-pulse" />;
  }
  return (
    <a href={src} target="_blank" rel="noopener noreferrer">
      <img src={src} alt="" className="w-12 h-12 object-cover rounded border border-slate-200 hover:opacity-90" />
    </a>
  );
}
